import React, { Suspense } from "react";
import { EventNotFound } from "./components/EventNotFound";
import { RESERVED_SLUGS } from "./config";

// Code splitting : chaque sous-app (orga, photographe, admin) en chunk
// dynamique -> orga ne charge pas le bundle admin et vice-versa. Reduit
// le TTI initial sur /orga (route la plus visitee). CRA supporte React.lazy
// nativement via les imports dynamiques webpack.
const OrganizerApp = React.lazy(() => import("./orga/OrganizerApp"));
const PhotographerApp = React.lazy(() => import("./photographer/PhotographerApp"));
const AdminApp = React.lazy(() => import("./AdminApp"));

function LazyBoundary({ children }) {
  return (
    <Suspense fallback={<div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#7B2FFF", fontFamily: "system-ui", fontSize: 14 }}>Chargement…</div>}>
      {children}
    </Suspense>
  );
}

// Router manuel segments-based. Pas de react-router (dep evitee).
//
// Routes effectives :
//   /admin                    -> AdminApp (console interne Will)
//   /orga                     -> OrganizerApp (login + dashboard orga)
//   /photographe[/...]        -> PhotographerApp (login + event + upload)
//
// Routes 301 (CF Pages public/_redirects, interceptees AVANT React) :
//   /events           -> https://will-app.com/
//   /events/{slug}    -> https://will-app.com/event/{slug}
//
// Routes retro-compat (anciens liens internes/externes) :
//   /dashboard/admin          -> /admin
//   /dashboard/orga           -> /orga
//   /dashboard                -> /orga
//   /event                    -> vitrine /
//   /{slug}/galerie           -> vitrine /event/{slug}
//   ?gallery=1&event={slug}   -> vitrine /event/{slug}
//   /                         -> vitrine /
export default function App() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const segments = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);

  if (segments[0] === "admin") return <LazyBoundary><AdminApp /></LazyBoundary>;
  if (segments[0] === "orga") return <LazyBoundary><OrganizerApp /></LazyBoundary>;
  if (segments[0] === "photographe") return <LazyBoundary><PhotographerApp /></LazyBoundary>;

  if (segments[0] === "dashboard" && segments[1] === "admin") {
    window.location.replace("/admin"); return null;
  }
  if (segments[0] === "dashboard" && segments[1] === "orga") {
    window.location.replace("/orga"); return null;
  }
  if (segments.length === 1 && segments[0] === "dashboard") {
    window.location.replace("/orga"); return null;
  }

  // Legacy : /event seul (singulier sans slug) → vitrine /.
  if (segments.length === 1 && segments[0] === "event") {
    window.location.replace("https://will-app.com/"); return null;
  }

  // Legacy : /{slug}/galerie → vitrine /event/{slug}.
  if (segments.length === 2 && segments[1] === "galerie") {
    if (RESERVED_SLUGS.has(segments[0])) return <EventNotFound />;
    window.location.replace(`https://will-app.com/event/${encodeURIComponent(segments[0])}`);
    return null;
  }

  // Legacy : ?gallery=1&event={slug} → vitrine /event/{slug}.
  if (params.get("gallery") === "1") {
    const slug = params.get("event") || "";
    if (slug && !RESERVED_SLUGS.has(slug)) {
      window.location.replace(`https://will-app.com/event/${encodeURIComponent(slug)}`);
      return null;
    }
    return <EventNotFound />;
  }

  // Root : redirige vers la vitrine.
  if (segments.length === 0) {
    window.location.replace("https://will-app.com/"); return null;
  }

  return <NotFound />;
}

function NotFound() {
  React.useEffect(() => { document.title = "Will – Page introuvable"; }, []);
  return (
    <div style={{ minHeight:"100vh", background:"#F5F3FF", fontFamily:"DM Sans, sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ textAlign:"center", maxWidth:420 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🛣️</div>
        <div style={{ fontSize:24, fontWeight:700, color:"#1a1a2e", marginBottom:8 }}>Page introuvable</div>
        <div style={{ fontSize:14, color:"#6b6b80", marginBottom:20 }}>L'adresse n'existe pas ou plus.</div>
        <a href="https://will-app.com/" style={{ color:"#7B2FFF", fontSize:14, fontWeight:600, textDecoration:"none" }}>
          ← Accueil
        </a>
      </div>
    </div>
  );
}
